import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Input } from '../ui/Input';
import { AvatarCropModal } from '../ui/AvatarCropModal';
import { useAuthStore } from '../../store/useAuthStore';
import {
  AI_AGENT_MODELS,
  AI_AGENT_REASONING,
  type AiAgentGender,
  type AiAgentModel,
  type AiAgentReasoning,
  type AiAgentSettings,
  aiAgentPortraitSrc,
  getAiAgentSettings,
  setAiAgentEnabled,
  updateAiAgentSettings,
  uploadAiAgentPortrait,
} from '../../services/cabinet/aiAgentSettings';
import { cn } from '../../lib/cn';

const QUERY_KEY = ['cabinet', 'ai-agent'] as const;

const selectClass =
  'w-full h-10 px-3 text-sm rounded-cime border border-neutral-200 bg-white text-neutral-900 focus:outline-none focus:ring-2 focus:ring-cime-primary focus:border-transparent';

export function AgentSettingsTab() {
  const updatedBy = useAuthStore((s) => s.collaborator?.id ?? null);
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [cropUrl, setCropUrl] = useState<string | null>(null);
  const [draft, setDraft] = useState<AiAgentSettings | null>(null);

  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: getAiAgentSettings,
  });

  useEffect(() => {
    if (query.data) setDraft(query.data);
  }, [query.data]);

  const save = useMutation({
    mutationFn: (input: AiAgentSettings) => updateAiAgentSettings(input, updatedBy),
    onSuccess: (next) => {
      queryClient.setQueryData(QUERY_KEY, next);
      setDraft(next);
      toast.success('Agent IA mis à jour');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const portrait = useMutation({
    mutationFn: (file: File) => uploadAiAgentPortrait(file, updatedBy),
    onSuccess: (photoUrl) => {
      queryClient.setQueryData(QUERY_KEY, (prev: AiAgentSettings | undefined) =>
        prev ? { ...prev, photoUrl } : prev,
      );
      setDraft((prev) => (prev ? { ...prev, photoUrl } : prev));
      toast.success('Portrait enregistré');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  async function openRecrop(src: string) {
    if (src.startsWith('/') || src.startsWith('blob:')) {
      setCropUrl(src);
      return;
    }
    try {
      const res = await fetch(src);
      if (!res.ok) throw new Error('portrait');
      const blob = await res.blob();
      setCropUrl(URL.createObjectURL(blob));
    } catch {
      toast.error('Impossible de relire ce portrait. Choisissez à nouveau le fichier.');
    }
  }

  function openFile(file: File) {
    if (!file.type.startsWith('image/')) {
      toast.error('Choisissez une image.');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Image trop lourde. Maximum 8 Mo.');
      return;
    }
    setCropUrl(URL.createObjectURL(file));
  }

  function closeCrop() {
    if (cropUrl?.startsWith('blob:')) URL.revokeObjectURL(cropUrl);
    setCropUrl(null);
  }

  async function onCropped(blob: Blob) {
    closeCrop();
    const file = new File([blob], 'portrait.jpg', { type: 'image/jpeg' });
    await portrait.mutateAsync(file);
  }

  if (query.isLoading || !draft) {
    return <p className="text-sm text-neutral-500">Chargement…</p>;
  }

  const src = aiAgentPortraitSrc(draft.photoUrl);

  return (
    <Card className="max-w-2xl">
      <div className="space-y-6">
      <div>
        <h2 className="text-base font-semibold text-neutral-900">Agent IA</h2>
        <p className="mt-1 text-sm text-neutral-500">
          Coupé, l’assistant disparaît des messages directs. Le portrait se recadre en rond, comme sur la fiche profil.
        </p>
      </div>

      <div className="flex items-center justify-between gap-4">
        <span className="text-sm text-neutral-800">Assistant dans la messagerie</span>
        <button
          type="button"
          role="switch"
          aria-checked={draft.enabled}
          aria-label="Assistant IA"
          onClick={() => {
            const enabled = !draft.enabled;
            setDraft({ ...draft, enabled });
            setAiAgentEnabled(enabled, updatedBy)
              .then((next) => {
                queryClient.setQueryData(QUERY_KEY, next);
                setDraft(next);
              })
              .catch((err: Error) => {
                setDraft(query.data ?? draft);
                toast.error(err.message);
              });
          }}
          className={cn(
            'relative h-6 w-11 shrink-0 rounded-full transition-colors',
            draft.enabled ? 'bg-cime-primary' : 'bg-neutral-300',
          )}
        >
          <span
            className={cn(
              'absolute top-0.5 h-5 w-5 rounded-full bg-white transition-[left]',
              draft.enabled ? 'left-5' : 'left-0.5',
            )}
          />
        </button>
      </div>

      <div className="flex items-center gap-5">
        <img
          src={src}
          alt=""
          className="h-20 w-20 rounded-full object-cover border border-neutral-200 bg-neutral-100"
        />
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="secondary" size="sm" onClick={() => fileRef.current?.click()}>
            Changer la photo
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={portrait.isPending}
            onClick={() => { void openRecrop(src); }}
          >
            Recadrer
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) openFile(file);
              e.target.value = '';
            }}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          label="Nom"
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
        />
        <label className="flex flex-col gap-1 text-sm font-medium text-neutral-700">
          Genre
          <select
            className={selectClass}
            value={draft.gender}
            onChange={(e) => setDraft({ ...draft, gender: e.target.value as AiAgentGender })}
          >
            <option value="female">Femme</option>
            <option value="male">Homme</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm font-medium text-neutral-700">
          Modèle
          <select
            className={selectClass}
            value={draft.model}
            onChange={(e) => setDraft({ ...draft, model: e.target.value as AiAgentModel })}
          >
            {AI_AGENT_MODELS.map((m) => (
              <option key={m.id} value={m.id}>{m.label}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm font-medium text-neutral-700">
          Force du raisonnement
          <select
            className={selectClass}
            value={draft.reasoning}
            onChange={(e) => setDraft({ ...draft, reasoning: e.target.value as AiAgentReasoning })}
          >
            {AI_AGENT_REASONING.map((m) => (
              <option key={m.id} value={m.id}>{m.label}</option>
            ))}
          </select>
        </label>
      </div>

      <div>
        <Button
          type="button"
          disabled={save.isPending || !draft.name.trim()}
          isLoading={save.isPending}
          onClick={() => save.mutate(draft)}
        >
          Enregistrer
        </Button>
      </div>

      {cropUrl && (
        <AvatarCropModal
          imageUrl={cropUrl}
          onCancel={closeCrop}
          onCropComplete={(blob) => { void onCropped(blob); }}
        />
      )}
      </div>
    </Card>
  );
}
